import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { amorcageApi, comptaApi, tacheInconnue, type AnalyseAmorcage } from '../services/compta-api';
import { Spinner } from '../components/shared/Spinner';
import { quandCompta } from '../lib/dates-compta';

/**
 * Initialisation du registre — opération unique.
 *
 * Sans elle, la première clôture ferait remonter tout l'historique de Books.
 * Elle inscrit ce qui est DÉJÀ parti chez KPMG : toutes les pièces antérieures
 * aux mois envoyés à la main, puis, sur ces mois, exactement les pièces des
 * fichiers déposés.
 *
 * Deux garde-fous, qui viennent de la comparaison faite le 22/09/2026 :
 *   - un fichier manquant BLOQUE, parce que ses pièces repartiraient en
 *     doublon chez KPMG. Seule exception : un mois sans avoir, confirmé ici ;
 *   - une pièce introuvable dans Books doit être TRANCHÉE une par une, sans
 *     quoi une seule pièce supprimée après envoi bloquerait l'opération sans
 *     issue.
 *
 * Les classeurs déposés ne sont pas conservés : ce sont des données comptables
 * nominatives, lues le temps de l'analyse.
 */

const LIBELLES: Record<string, string> = {
  factures_clients: 'Factures clients',
  avoirs_clients: 'Avoirs clients',
  factures_fournisseurs: 'Factures fournisseurs',
  avoirs_fournisseurs: 'Avoirs fournisseurs',
};

const euros = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Les mois de juin au dernier mois envoyé à la main, inclus. */
function moisJusqua(dernier: string): string[] {
  const out: string[] = [];
  const [a0, m0] = '2026-06'.split('-').map(Number);
  const [a1, m1] = dernier.split('-').map(Number);
  let a = a0!;
  let m = m0!;
  while (a < a1! || (a === a1! && m <= m1!)) {
    out.push(`${a}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      a += 1;
    }
    if (out.length > 36) break;
  }
  return out;
}

/**
 * LE RECHARGEMENT NE DOIT PLUS TOUT PERDRE (incident du 13/10/2026).
 *
 * Valérie a rechargé la page pendant une analyse : les fichiers choisis sont
 * partis — un navigateur ne peut pas les garder, c'est irrémédiable — mais le
 * SUIVI aussi, alors que la tâche continuait côté serveur. Deux parades :
 * prévenir avant de quitter, et retrouver la tâche au retour.
 *
 * L'identifiant vit dans le stockage de l'ONGLET : il ne survit pas à sa
 * fermeture, ce qui est exactement la durée de vie utile, il n'est qu'un
 * identifiant opaque, et deux onglets ne se marchent pas dessus.
 */
const CLE_TACHE = 'compta.amorcage.tache';

/**
 * LA DURÉE ÉCOULÉE, parce qu'un avancement sans durée ne dit pas si ça avance.
 *
 * L'écran affichait la dernière étape atteinte, et rien d'autre. Or les étapes
 * de l'analyse durent longtemps : « Référentiel clients : 12 000 chargés… »
 * reste à l'écran plusieurs minutes sans bouger. Valérie ne pouvait donc pas
 * distinguer sept minutes normales d'un traitement bloqué, et c'est la question
 * qu'on se pose quand on attend devant un écran.
 */
export function dureeAttente(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  // Les secondes sont sur deux chiffres : « 7 min 05 s », pour que la durée ne
  // change pas de largeur à chaque battement et ne fasse pas sauter la ligne.
  return `${minutes} min ${String(total % 60).padStart(2, '0')} s`;
}

/**
 * Au-delà, l'écran dit que c'est LONG — sans affirmer que c'est cassé.
 *
 * Douze minutes, soit environ le double des 381 secondes mesurées en production
 * le 13/10/2026 pour l'analyse (et une initialisation complète, analyse puis
 * inscription, dure un peu plus). En deçà, l'attente est NORMALE et un
 * avertissement ne ferait qu'inquiéter à tort ; au-delà, elle sort du connu, et
 * c'est l'information utile : que le travail se poursuit côté serveur, et qu'il
 * n'y a rien à cliquer. Bien plus tôt, donc, que les deux heures au bout
 * desquelles le serveur abandonne une tâche (`ABANDON_MS`) : dire « c'est long »
 * ne coûte rien, oublier une tâche vivante coûterait l'initialisation.
 */
const SEUIL_ATTENTE_MS = 12 * 60 * 1000;

type TacheSuivie = { id: string; quoi: 'analyse' | 'lancement' };

/**
 * La tâche d'un rechargement précédent, lue AVANT le premier rendu.
 *
 * Passer par un effet ne marchait pas : React exécute les effets dans l'ordre
 * de déclaration, et celui qui ÉCRIT efface la clé quand aucune tâche n'est en
 * cours — c'est-à-dire au montage, avant que celui qui LIT ne s'exécute.
 *
 * `quoi` est validé contre ses deux seules valeurs : une valeur trafiquée dans
 * le stockage tombait sinon dans la branche « lancement » et affichait
 * « undefined pièces inscrites ».
 */
function tacheRetrouvee(): TacheSuivie | null {
  try {
    const brut = sessionStorage.getItem(CLE_TACHE);
    if (!brut) return null;
    const r = JSON.parse(brut) as Partial<TacheSuivie>;
    if (typeof r?.id !== 'string' || !r.id) return null;
    if (r.quoi !== 'analyse' && r.quoi !== 'lancement') return null;
    return { id: r.id, quoi: r.quoi };
  } catch {
    return null;
  }
}

export function ComptaInitialisationPage() {
  const [dernierMois, setDernierMois] = useState('2026-08');
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [sansAvoir, setSansAvoir] = useState<Array<{ mois: string; cle: string }>>([]);
  const [ignorees, setIgnorees] = useState<string[]>([]);
  const [renumerotations, setRenumerotations] = useState<Array<{ cle: string; idBooks: string }>>([]);
  const [analyse, setAnalyse] = useState<AnalyseAmorcage | null>(null);
  /**
   * L'identifiant de la lecture de Books faite par l'analyse.
   *
   * Le lancement le renvoie, et le serveur s'épargne ainsi six minutes de
   * relecture. C'est un identifiant OPAQUE : la lecture elle-même n'a jamais
   * quitté le serveur, donc la règle « le registre ne se fonde pas sur ce qui a
   * transité par le navigateur » reste entière — l'appariement, lui, est
   * toujours refait côté serveur à partir des classeurs redéposés.
   */
  const [instantaneId, setInstantaneId] = useState<string | undefined>(undefined);
  const [erreur, setErreur] = useState<string | null>(null);
  const [fait, setFait] = useState<{ inscrites: number } | null>(null);

  const qc = useQueryClient();
  const mois = useMemo(() => moisJusqua(dernierMois), [dernierMois]);
  const etat = useQuery({ queryKey: ['compta', 'exports'], queryFn: comptaApi.liste });

  const payload = () => ({ fichiers, mois, sansAvoir, ignorees, renumerotations, instantaneId });

  /**
   * LE SUIVI D'UNE TÂCHE DE FOND, et pourquoi cet écran en a besoin.
   *
   * L'analyse lit tout Books en résumé : 381 secondes mesurées en production le
   * 13/10/2026, pour une passerelle qui coupe à 30. L'écran attendait une
   * réponse qui ne pouvait pas arriver, affichait un « 504 » rouge, et comme le
   * bouton « Lancer » exige le résultat de l'analyse, l'initialisation était
   * impossible à mener à son terme. Le serveur rend donc la main tout de suite,
   * et c'est ici qu'on regarde où il en est — toutes les 2 secondes, comme
   * l'écran des clôtures le fait déjà pour une génération.
   */
  const [tache, setTache] = useState<TacheSuivie | null>(tacheRetrouvee);
  const [progression, setProgression] = useState<string | null>(tache ? 'Reprise du suivi…' : null);
  /**
   * L'instant du lancement, tel que le SERVEUR le donne (`depuis`).
   *
   * Il n'est pas posé au clic : après un rechargement de la page, le compteur
   * repartirait de zéro alors que la tâche travaille depuis cinq minutes — et
   * c'est justement là qu'on regarde la durée. Il reste donc `null` jusqu'à la
   * première réponse de suivi (2 secondes), où « Démarrage… » suffit.
   */
  const [depuis, setDepuis] = useState<number | null>(null);
  const [maintenant, setMaintenant] = useState(() => Date.now());

  const suivi = useQuery({
    queryKey: ['compta', 'amorcage', 'tache', tache?.id],
    queryFn: () => amorcageApi.suivre(tache!.id),
    enabled: Boolean(tache),
    refetchInterval: (q) => (q.state.data?.statut === 'en_cours' ? 2000 : false),
    // Une tâche INCONNUE est une réponse, pas un incident : on n'insiste pas.
    // Mais un appel raté sur les ~180 que dure le travail (hoquet réseau,
    // redéploiement) ne doit pas faire abandonner le suivi d'une tâche qui
    // tourne toujours : on réessaie plusieurs fois avant de renoncer.
    retry: (essais, err) => !tacheInconnue(err) && essais < 5,
    retryDelay: 2000,
  });

  useEffect(() => {
    const etat = suivi.data;
    if (!etat || !tache) return;
    if (etat.statut === 'en_cours') {
      setProgression(etat.progression);
      setDepuis(etat.depuis);
      return;
    }
    const enCours = tache;
    setTache(null);
    setProgression(null);
    setDepuis(null);
    if (etat.statut === 'echec') {
      if (enCours.quoi === 'analyse') setAnalyse(null);
      setErreur(etat.progression ? `${etat.erreur} (dernière étape atteinte : ${etat.progression})` : etat.erreur);
      // ON RELIT L'ÉTAT DU REGISTRE, même sur un échec — c'est ici que ça
      // compte, et non dans le `onError` de la mutation qui ne se déclenche
      // plus (le serveur répond 202 tout de suite). Une tâche peut échouer
      // APRÈS avoir inscrit les pièces : si on ne relit pas, l'écran afficherait
      // du rouge et ne dirait jamais que l'initialisation a eu lieu.
      void qc.invalidateQueries({ queryKey: ['compta', 'exports'] });
      return;
    }
    setErreur(null);
    if (enCours.quoi === 'analyse') {
      const r = etat.resultat as AnalyseAmorcage & { instantaneId?: string };
      setAnalyse(r);
      setInstantaneId(r.instantaneId);
    } else {
      setFait({ inscrites: (etat.resultat as { inscrites: number }).inscrites });
      void qc.invalidateQueries({ queryKey: ['compta', 'exports'] });
    }
  }, [suivi.data, tache, qc]);

  // Une erreur de SUIVI (tâche inconnue, session expirée) doit se voir : sans
  // cela, l'écran resterait sur « Démarrage… » pour toujours.
  useEffect(() => {
    if (!suivi.isError) return;
    setTache(null);
    setProgression(null);
    setDepuis(null);
    setErreur(
      tacheInconnue(suivi.error)
        ? suivi.error instanceof Error
          ? suivi.error.message
          : 'Ce traitement n’est plus suivi. Redéposez vos fichiers.'
        : 'Le suivi du traitement a été perdu (réseau ou serveur injoignable). Le travail se poursuit peut-être côté serveur : rechargez l’écran pour voir où en est le registre.',
    );
    // Le registre est la seule source de vérité : on le relit, perdre le suivi
    // ne dit RIEN de ce que le serveur a fait ou non.
    void qc.invalidateQueries({ queryKey: ['compta', 'exports'] });
  }, [suivi.isError, suivi.error, qc]);

  // La tâche retrouvée est lue AVANT le premier rendu (initialiseur de
  // `useState`, plus haut) : un effet de lecture serait exécuté APRÈS l'effet
  // d'écriture ci-dessous, qui efface la clé quand aucune tâche n'est en
  // cours — c'est-à-dire au montage. La reprise ne marchait donc jamais
  // (audit du 14/10/2026). Il ne reste qu'un seul effet, celui qui écrit.
  useEffect(() => {
    try {
      if (tache) sessionStorage.setItem(CLE_TACHE, JSON.stringify(tache));
      else sessionStorage.removeItem(CLE_TACHE);
    } catch {
      // Navigation privée, stockage refusé : sans conséquence, l'écran
      // fonctionne, il ne saura simplement pas reprendre après un rechargement.
    }
  }, [tache]);

  const analyser = useMutation({
    mutationFn: () => amorcageApi.analyser(payload()),
    onSuccess: (r) => {
      setErreur(null);
      setAnalyse(null);
      setInstantaneId(undefined);
      setProgression('Démarrage…');
      setDepuis(null);
      setTache({ id: r.tacheId, quoi: 'analyse' });
    },
    onError: (e: Error) => {
      setAnalyse(null);
      setErreur(e.message);
    },
  });
  const lancer = useMutation({
    mutationFn: () => amorcageApi.lancer(payload()),
    onSuccess: (r) => {
      setErreur(null);
      setProgression('Démarrage…');
      setDepuis(null);
      setTache({ id: r.tacheId, quoi: 'lancement' });
    },
    onError: (e: Error) => {
      setErreur(e.message);
      // Le serveur a peut-être abouti malgré une réponse coupée : on relit
      // l'état du registre, qui est la seule source de vérité.
      void qc.invalidateQueries({ queryKey: ['compta', 'exports'] });
    },
  });

  /**
   * UN BATTEMENT PAR SECONDE, tant qu'une tâche tourne.
   *
   * Le suivi, lui, n'interroge le serveur que toutes les 2 secondes, et ne
   * provoque de rendu que si l'étape a CHANGÉ — or elle ne change pas pendant
   * des minutes. Sans ce battement, la durée affichée resterait figée, ce qui
   * donne exactement l'impression qu'on cherche à dissiper. Il ne coûte rien et
   * s'arrête avec la tâche (aucun minuteur qui tourne pour rien).
   */
  useEffect(() => {
    if (depuis === null) return;
    setMaintenant(Date.now());
    const battement = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => clearInterval(battement);
  }, [depuis]);

  /**
   * La durée écoulée, bornée à zéro : `depuis` vient de l'horloge du SERVEUR et
   * la comparaison se fait avec celle du navigateur. Les deux sont synchronisées
   * en pratique, mais un poste en avance de quelques secondes afficherait sinon
   * une durée négative — « -3 s écoulées » ferait douter de tout l'écran.
   */
  const attenteMs = depuis === null ? null : Math.max(0, maintenant - depuis);

  /** Un travail est en cours : les boutons attendent, et l'écran le dit. */
  const travailEnCours = Boolean(tache) || analyser.isPending || lancer.isPending;

  /**
   * Prévenir avant un RECHARGEMENT ou la fermeture de l'onglet — pas avant une
   * navigation dans Gertrude, que `beforeunload` ne voit pas.
   *
   * La condition porte sur ce qui est réellement PERDABLE : les fichiers
   * déposés. Pas sur le travail en cours, qui se poursuit côté serveur (c'est
   * ce que l'écran affiche, et l'avertissement le contredisait) ; et plus du
   * tout une fois l'initialisation faite, où il ne restait qu'à inquiéter sur
   * une opération déjà close. Les deux cas venaient de l'audit du 14/10/2026.
   */
  useEffect(() => {
    if (fichiers.length === 0 || fait) return;
    const avertir = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Les navigateurs imposent leur propre texte ; seule la présence de
      // l'avertissement est de notre ressort.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avertir);
    return () => window.removeEventListener('beforeunload', avertir);
  }, [fichiers.length, fait]);


  const nonTranchees = (analyse?.introuvables ?? []).filter((i) => !ignorees.includes(i.cle) && !renumerotations.some((r) => r.cle === i.cle));
  // Les FICHIERS sont exigés, pas seulement l'analyse : après une reprise de
  // suivi, l'analyse est là mais les classeurs sont perdus (le navigateur ne
  // les garde pas). Proposer « Lancer » enverrait un formulaire sans fichiers,
  // que le serveur refuserait — autant le dire avant.
  const pretALancer =
    analyse && fichiers.length > 0 && !analyse.manquants.length && !nonTranchees.length && !travailEnCours;

  if (etat.isLoading) return <Spinner />;
  // Sans ce test, un échec de lecture laissait `registreAmorce` indéfini : le
  // formulaire d'initialisation s'affichait alors sur un registre peut-être
  // DÉJÀ amorcé, et seul le refus du serveur l'arrêtait.
  if (etat.isError)
    return (
      <div className="p-4 md:p-6">
        <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Initialisation du registre</h1>
        <p className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-800 ring-1 ring-red-200">
          L’état du registre n’a pas pu être lu : {(etat.error as Error).message}. L’initialisation n’est pas proposée tant qu’on ignore si elle a déjà eu lieu.
        </p>
      </div>
    );

  if (etat.data?.registreAmorce && !fait) {
    /**
     * CE QUI A ÉTÉ FAIT, et pas seulement « ça a eu lieu ».
     *
     * L'ancien bandeau disait l'opération faite sans dire quand, par qui, avec
     * quels classeurs ni combien de pièces : Valérie a dû demander pour le
     * savoir (13/10/2026). Or tout était DÉJÀ enregistré — l'initialisation
     * écrit le nom, le mois, le type et le nombre de pièces de chaque fichier
     * déposé. Il manquait seulement de l'afficher.
     */
    const amorce = etat.data.exports.find((e) => e.totaux?.amorcage && e.statut !== 'annule');
    const classeurs = amorce?.totaux?.fichiers ?? [];
    return (
      <div className="space-y-4 p-4 md:p-6">
        <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Initialisation du registre</h1>
        <p className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-900 ring-1 ring-emerald-200">
          <strong>L’initialisation a eu lieu</strong> — elle n’a lieu qu’une fois.
          {amorce && (
            <>
              {' '}
              {amorce.pieces} pièce(s) inscrites au registre le {quandCompta(amorce.genere_at)} par {amorce.genere_par}
              {typeof amorce.totaux?.avantLesFichiers === 'number' && (
                <>
                  , dont {amorce.totaux.avantLesFichiers} antérieures aux classeurs déposés (inscrites en bloc, faute
                  de fichier pour les vérifier)
                </>
              )}
              .
            </>
          )}
        </p>

        {classeurs.length > 0 && (
          <section className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="font-[Sora] text-lg font-semibold text-[#384872]">Les classeurs qui ont servi</h2>
            <p className="mt-1 text-xs text-slate-500">
              Leur contenu n’est pas conservé — seulement de quoi savoir ce qui a été traité.
            </p>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {classeurs.map((f) => (
                <li key={`${f.mois}-${f.cle}-${f.nom}`} className="flex flex-wrap gap-x-2">
                  <span className="font-medium text-[#384872]">{f.mois}</span>
                  <span>{LIBELLES[f.cle] ?? f.cle}</span>
                  <span className="text-slate-500">— {f.pieces} pièce(s)</span>
                  <span className="text-slate-500">({f.nom})</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {amorce && (amorce.totaux?.ignorees?.length || amorce.totaux?.renumerotations?.length) ? (
          <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
            <h2 className="font-[Sora] text-lg font-semibold text-[#384872]">Ce qui a été tranché à la main</h2>
            {amorce.totaux?.ignorees?.length ? (
              <p className="mt-2">
                <strong>{amorce.totaux.ignorees.length} pièce(s) ignorées</strong> : elles n’existaient plus dans
                Books, et leur absence a été assumée.
              </p>
            ) : null}
            {amorce.totaux?.renumerotations?.length ? (
              <p className="mt-2">
                <strong>{amorce.totaux.renumerotations.length} renumérotation(s)</strong> acceptées : la pièce de Books
                a été inscrite à la place du numéro du fichier.
              </p>
            ) : null}
          </section>
        ) : null}
      </div>
    );
  }

  if (fait) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Initialisation du registre</h1>
        <p className="mt-4 rounded-md bg-emerald-50 p-4 text-sm text-emerald-900 ring-1 ring-emerald-200">
          <strong>{fait.inscrites} pièces inscrites au registre.</strong> Les pièces jamais envoyées n’y sont pas : elles remonteront dans la prochaine clôture, comme prévu.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header>
        <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Initialisation du registre</h1>
        {/* L'état D'ABORD. L'écran montrait un formulaire vide sans jamais dire
            si quelque chose avait déjà été traité : Valérie a dû le demander
            (13/10/2026). Une ANALYSE n'écrit rien, donc on peut en faire autant
            qu'on veut. Un LANCEMENT interrompu, lui, laisse une ligne d'export
            ANNULÉE, visible sur l'écran des clôtures avec son motif — mais ses
            pièces sont libérées, donc le registre est bien vide et
            l'initialisation reste à faire. C'est ce que le message dit. */}
        <p className="mt-2 rounded-md bg-[#A6D3F9]/30 p-3 text-sm text-[#384872]">
          <strong>Le registre est vide : aucune initialisation n’a eu lieu.</strong> Vous pouvez analyser autant de
          fois que nécessaire — l’analyse n’écrit rien. Seul le bouton « Lancer l’initialisation » inscrit les pièces.
        </p>
        <p className="mt-2 text-sm text-slate-600">
          À faire une seule fois, après le dernier envoi fait à la main et avant la première clôture produite par Gertrude.
        </p>
      </header>

      <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Dernier mois envoyé à la main</span>
          <input type="month" value={dernierMois} onChange={(e) => setDernierMois(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2" />
          <span className="ml-2 text-xs text-slate-500">Les fichiers attendus vont de juin 2026 à ce mois.</span>
        </label>

        <div>
          <span className="mb-1 block text-sm font-medium text-slate-700">Fichiers envoyés à KPMG</span>
          <input
            type="file"
            multiple
            accept=".xlsx"
            onChange={(e) => {
              setFichiers(Array.from(e.target.files ?? []));
              setAnalyse(null);
            }}
            className="text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">
            Les classeurs sont lus le temps de l’analyse, puis oubliés : ils ne sont jamais conservés dans Gertrude.
            {' '}
            <strong>Ne rechargez pas cette page</strong> tant que vous n’avez pas lancé l’initialisation : un navigateur
            ne peut pas garder des fichiers choisis, il faudrait les redéposer.
          </p>
        </div>

        <fieldset className="rounded-md bg-slate-50 p-3">
          <legend className="px-1 text-sm font-medium text-slate-700">Mois sans avoir</legend>
          <p className="mb-2 text-xs text-slate-600">Un fichier d’avoirs absent bloque, sauf si vous confirmez qu’aucun n’a été envoyé ce mois-là. Les factures, elles, sont toujours exigées.</p>
          <div className="space-y-1">
            {mois.map((m) => (
              <div key={m} className="flex flex-wrap items-center gap-4 text-sm">
                <span className="w-24 text-slate-700">{m}</span>
                {(['avoirs_clients', 'avoirs_fournisseurs'] as const).map((cle) => {
                  const coche = sansAvoir.some((s) => s.mois === m && s.cle === cle);
                  return (
                    <label key={cle} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={coche}
                        onChange={(e) => {
                          setAnalyse(null);
                          setSansAvoir((s) => (e.target.checked ? [...s, { mois: m, cle }] : s.filter((x) => !(x.mois === m && x.cle === cle))));
                        }}
                      />
                      aucun {LIBELLES[cle]!.toLowerCase()}
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </fieldset>

        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => analyser.mutate()} disabled={!fichiers.length || travailEnCours} className="rounded-md bg-[#384872] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {tache?.quoi === 'analyse' || analyser.isPending ? 'Analyse en cours…' : travailEnCours ? 'Veuillez patienter…' : 'Analyser les fichiers'}
          </button>
          {/* Le temps annoncé était « une minute environ » : c'était faux, et
              c'est ce qui rendait l'attente inquiétante. L'analyse a mis six
              minutes en production. On annonce donc large, et surtout on
              AFFICHE l'avancement juste en dessous. */}
          <span className="text-xs text-slate-500">
            L’analyse lit tout Books en résumé : <strong>plusieurs minutes</strong>, l’avancement s’affiche ci-dessous.
            Elle n’écrit rien.
          </span>
        </div>
        {progression && (
          <div className="rounded-md bg-[#A6D3F9]/30 p-3 text-sm text-[#384872]" aria-live="polite">
            <p>
              <span className="font-medium">En cours…</span> {progression}
              {/* LA DURÉE, à côté de l'étape : c'est elle qui dit si ça avance.
                  Une étape immobile depuis deux minutes n'inquiète pas, la même
                  depuis vingt minutes se regarde autrement. */}
              {attenteMs !== null && (
                <>
                  {' — '}
                  <strong>{dureeAttente(attenteMs)}</strong> écoulées
                </>
              )}
            </p>
            <p className="mt-1 text-xs text-[#384872]">
              Vous pouvez laisser cet écran ouvert. Le travail continue côté serveur même si vous le quittez.
            </p>
            {attenteMs !== null && attenteMs > SEUIL_ATTENTE_MS && (
              <p className="mt-2 rounded bg-[#F6C847]/30 p-2 text-xs text-[#926B00] ring-1 ring-[#F6C847]">
                <strong>C’est plus long que d’habitude</strong> (l’analyse a mis un peu plus de 6 minutes en
                production). Rien à cliquer : le travail se poursuit côté serveur, et l’étape ci-dessus avance dès
                qu’il a fini la sienne. Si la même étape reste affichée très longtemps, prévenez avant de relancer —
                une analyse ne coûte qu’un nouveau dépôt des fichiers, mais une initialisation n’a lieu qu’une fois.
              </p>
            )}
          </div>
        )}
        {erreur && <p className="rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">{erreur}</p>}
      </section>

      {analyse && (
        <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="font-[Sora] text-lg font-semibold text-[#384872]">Ce qui sera inscrit</h2>
          <p className="text-sm text-slate-700">
            <strong>{analyse.appariees}</strong> pièces au total, dont <strong>{analyse.avantLesFichiers}</strong> antérieures aux fichiers déposés (inscrites en bloc, faute de fichier pour les vérifier).
          </p>

          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2">Fichier</th>
                <th className="py-2">Rapport</th>
                <th className="py-2">Mois</th>
                <th className="py-2">Pièces</th>
              </tr>
            </thead>
            <tbody>
              {analyse.fichiers.map((f) => (
                <tr key={f.nom} className="border-t border-slate-100">
                  <td className="py-2">{f.nom}</td>
                  <td className="py-2">{LIBELLES[f.cle] ?? f.cle}</td>
                  <td className="py-2">{f.mois}</td>
                  <td className="py-2">{f.entrees?.length ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Les deux constats s'affichent TOUJOURS, même à zéro : sur une
              opération qui ne se joue qu'une fois, « aucun fichier ne manque »
              doit se lire, et ne pas se deviner de l'absence de message. */}
          {analyse.manquants.length === 0 ? (
            <p className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-900 ring-1 ring-emerald-200">
              <strong>Aucun fichier ne manque</strong> : les quatre rapports de chaque mois sont déposés, ou déclarés sans avoir.
            </p>
          ) : (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-900 ring-1 ring-red-200">
              <p className="font-medium">{analyse.manquants.length} fichier(s) manquant(s) — le lancement est bloqué</p>
              <ul className="mt-1 space-y-0.5">
                {analyse.manquants.map((m) => (
                  <li key={`${m.mois}-${m.cle}`}>
                    {m.libelle} de {m.mois}
                    {m.facultatif ? ' — ou cochez « aucun » ci-dessus' : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analyse.introuvables.length === 0 ? (
            <p className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-900 ring-1 ring-emerald-200">
              <strong>Aucune pièce introuvable</strong> : chaque pièce des fichiers déposés a été retrouvée dans Books. Rien à trancher.
            </p>
          ) : (
            <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
              <p className="font-medium">{analyse.introuvables.length} pièce(s) des fichiers introuvable(s) dans Books</p>
              <p className="mb-2 text-xs">Chacune doit être tranchée : accepter la renumérotation proposée, ou l’ignorer en connaissance de cause. La décision est enregistrée.</p>
              <ul className="space-y-2">
                {analyse.introuvables.map((i) => {
                  const ignoree = ignorees.includes(i.cle);
                  const acceptee = renumerotations.some((r) => r.cle === i.cle);
                  return (
                    <li key={i.cle} className="rounded bg-white/70 p-2">
                      <div>
                        <strong>{i.numero}</strong> — {i.date}, {euros(i.montant)} € <span className="text-xs text-slate-500">({i.fichier})</span>
                      </div>
                      {i.probableRenumerotation && (
                        <label className="mt-1 flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={acceptee}
                            onChange={(e) =>
                              setRenumerotations((r) => (e.target.checked ? [...r, { cle: i.cle, idBooks: i.probableRenumerotation!.id }] : r.filter((x) => x.cle !== i.cle)))
                            }
                          />
                          <span>
                            Probablement renumérotée en <strong>{i.probableRenumerotation.numero}</strong> ({i.probableRenumerotation.tiers}, même date et même montant) — inscrire cette pièce
                          </span>
                        </label>
                      )}
                      <label className="mt-1 flex items-center gap-2">
                        <input type="checkbox" checked={ignoree} onChange={(e) => setIgnorees((g) => (e.target.checked ? [...g, i.cle] : g.filter((x) => x !== i.cle)))} />
                        <span>Ignorer : cette pièce n’existe plus dans Books (décision assumée et tracée)</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
            <button type="button" onClick={() => lancer.mutate()} disabled={!pretALancer} className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {tache?.quoi === 'lancement' || lancer.isPending ? 'Initialisation en cours…' : travailEnCours ? 'Veuillez patienter…' : 'Lancer l’initialisation'}
            </button>
            {!pretALancer && (
              <span className="text-xs text-slate-500">
                {analyse.manquants.length
                  ? 'Déposez les fichiers manquants.'
                  : nonTranchees.length
                    ? `${nonTranchees.length} pièce(s) restent à trancher.`
                    : fichiers.length === 0
                      ? 'Redéposez vos classeurs : un rechargement de la page les a perdus. L’analyse, elle, est conservée.'
                      : ''}
              </span>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
